// ============================================================================
// AREA MASTER SCREEN — MSTR-AREA-001
// ============================================================================
// Third level of the hierarchy. Introduces:
//   - 2-level cascading FK filter (Zone grand-parent → Region direct-parent)
//   - Region dropdown labels carry parent-zone context for disambiguation
//   - 3-mandatory-site lookup refetches (handleAddNew / handleEdit / handleRestoreModal)
//     for BOTH zones AND regions so cross-tab changes upstream propagate here.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAreas } from 'hooks/api/useAreas';
import { useRegions } from 'hooks/api/useRegions';
import { useZones } from 'hooks/api/useZones';
import { areaService } from 'api/services/areaService';
import type { Area, AreaSearchRequest } from 'api/types/area.types';
import AreaFormModal, { type RegionOption } from 'components/modals/modules/area-module/AreaFormModal';

type ViewMode = 'list' | 'grid';

interface AreaMasterScreenProps {
  /** Passed by main_layout when rendered as a tabbed module. */
  tabId?: string;
  moduleName?: string;
}

const AreaMasterScreen: React.FC<AreaMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // ----- Server-side query params -----
  const [queryParams, setQueryParams] = useState<AreaSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useAreas(queryParams);

  // ----- Parent lookups -----
  // Form modal: active regions (with zone context in label).
  const { data: regions, refetch: refetchRegions } = useRegions({ page: 1, pageSize: 200 });
  // Filter dropdown: all regions (active + inactive + deleted).
  const { data: allRegions, refetch: refetchAllRegions } = useRegions({
    page: 1, pageSize: 200, statusCode: 'ALL',
  });
  // Filter dropdown: all zones (grand-parent cascade) with status suffixes.
  const { data: allZones, refetch: refetchAllZones } = useZones({
    page: 1, pageSize: 100, statusCode: 'ALL',
  });

  // Set of zone ids that are active AND not deleted — used to cascade-filter
  // regions whose grand-parent zone has been deactivated. Without this, a region
  // that is locally "ACTIVE" but whose parent zone is INACTIVE / DELETED would
  // still appear selectable — producing orphaned hierarchies.
  const activeZoneIds = useMemo(() => {
    const set = new Set<string>();
    (allZones ?? []).forEach(z => {
      if (!z.deletedAt && z.statusCode === 'ACTIVE') set.add(z.id);
    });
    return set;
  }, [allZones]);

  // Active-region options shown in the form modal. Label includes parent-zone
  // so the user can disambiguate regions with similar codes across zones.
  // Regions are filtered to exclude ones whose parent zone is inactive/deleted —
  // the backend will also reject such assignments (defense in depth).
  //
  // Guard: when allZones hasn't loaded yet, show all regions (no filter) to
  // avoid hiding the dropdown's entire contents during initial render.
  const regionOptions: RegionOption[] = useMemo(() => {
    const source = (regions ?? []);
    const filtered = activeZoneIds.size === 0
      ? source
      : source.filter(r => activeZoneIds.has(r.zoneId));
    return filtered.map(r => ({
      value: r.id,
      label: `${r.regionCode} — ${r.regionName}${r.zoneName ? ` · ${r.zoneName}` : ''}`,
    }));
  }, [regions, activeZoneIds]);

  // Filter-panel Zone dropdown — all zones, status-suffixed.
  const zoneFilterOptions = useMemo(() => {
    const list = (allZones ?? []).map(z => {
      let suffix = '';
      if (z.deletedAt) suffix = ' (Deleted)';
      else if (z.statusCode && z.statusCode !== 'ACTIVE') suffix = ` (${z.statusCode})`;
      return { value: z.id, label: `${z.zoneCode} — ${z.zoneName}${suffix}` };
    });
    return [{ value: '', label: 'All zones' }, ...list];
  }, [allZones]);

  // Filter-panel Region dropdown — cascades off the selected zone filter.
  // When no zone is selected, show all regions; otherwise restrict to that zone.
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const regionFilterOptions = useMemo(() => {
    const source = (allRegions ?? []).filter(r => !zoneFilter || r.zoneId === zoneFilter);
    const list = source.map(r => {
      let suffix = '';
      if (r.deletedAt) suffix = ' (Deleted)';
      else if (r.statusCode && r.statusCode !== 'ACTIVE') suffix = ` (${r.statusCode})`;
      const zoneSuffix = !zoneFilter && r.zoneCode ? ` · ${r.zoneCode}` : '';
      return {
        value: r.id,
        label: `${r.regionCode} — ${r.regionName}${zoneSuffix}${suffix}`,
      };
    });
    return [{ value: '', label: 'All regions' }, ...list];
  }, [allRegions, zoneFilter]);

  // ----- View state -----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // ----- Filter panel state -----
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [areaCodeFilter, setAreaCodeFilter] = useState('');
  const [areaNameFilter, setAreaNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // If the selected zone filter changes, invalidate region filter that no longer
  // belongs under that zone (otherwise the user sees empty results with no cue).
  useEffect(() => {
    if (!zoneFilter || !regionFilter) return;
    const r = (allRegions ?? []).find(rx => rx.id === regionFilter);
    if (r && r.zoneId !== zoneFilter) setRegionFilter('');
  }, [zoneFilter, regionFilter, allRegions]);

  // Debounced global search → server
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ----- Form modal state -----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);

  // Seed region options — surface the parent region of the area under edit when
  // it's no longer in the active list (deactivated/deleted in another tab).
  // Look up the accurate state (INACTIVE vs DELETED) in `allRegions`.
  const seedRegionOptions: RegionOption[] = useMemo(() => {
    if (formMode !== 'edit' || !selectedArea) return [];
    const a = selectedArea;
    if (regionOptions.some(o => o.value === a.regionId)) return []; // already in active list

    const parentFromAll = (allRegions ?? []).find(r => r.id === a.regionId);
    let suffix = ' (Inactive)'; // fallback until allRegions loads
    if (parentFromAll) {
      if (parentFromAll.deletedAt) suffix = ' (Deleted)';
      else if (parentFromAll.statusCode && parentFromAll.statusCode !== 'ACTIVE') {
        suffix = ` (${parentFromAll.statusCode})`;
      } else {
        suffix = '';
      }
    }

    const zoneLabel = a.zoneName ? ` · ${a.zoneName}` : '';
    return [{
      value: a.regionId,
      label: `${a.regionCode ?? '?'} — ${a.regionName ?? 'Unknown'}${zoneLabel}${suffix}`,
    }];
  }, [formMode, selectedArea, regionOptions, allRegions]);

  // Minimize: each Add gets a unique instance id; auto-minimize current on new Add.
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      // Refetch parent FK lookups first — cross-tab changes to Zone / Region
      // must reflect in this modal's dropdown when restored.
      refetchRegions();
      refetchAllRegions();
      refetchAllZones();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(a => a.areaCode === recordCode);
        if (rec) setSelectedArea(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data, refetchRegions, refetchAllRegions, refetchAllZones]
  );

  // ----- Delete state -----
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<Area | null>(null);

  // ----- Handlers -----
  const handleAddNew = useCallback(() => {
    // Refetch parent FK lookups so dropdown reflects latest server state.
    refetchRegions();
    refetchAllRegions();
    refetchAllZones();
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedArea(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen, refetchRegions, refetchAllRegions, refetchAllZones]);

  const handleEdit = useCallback(
    (area: Area) => {
      refetchRegions();
      refetchAllRegions();
      refetchAllZones();
      const modalId = tabId ? `${tabId}-form-modal-edit-${area.areaCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedArea(area);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal, refetchRegions, refetchAllRegions, refetchAllZones]
  );

  const handleView = useCallback(
    (area: Area) => {
      const modalId = tabId ? `${tabId}-view-modal-${area.areaCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedArea(area);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (area: Area) => {
      setSelectedArea(area);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((area: Area) => {
    setAreaToDelete(area);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!areaToDelete) return;
    setIsDeleting(true);
    try {
      await areaService.delete(areaToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Area "${areaToDelete.areaCode}" deleted successfully.`);
      setAreaToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [areaToDelete, refetch]);

  const handleSaved = useCallback(() => {
    refetch();
    refetchRegions();
    refetchAllRegions();
    refetchAllZones();
  }, [refetch, refetchRegions, refetchAllRegions, refetchAllZones]);

  const handleRefresh = useCallback(() => {
    refetch();
    refetchRegions();
    refetchAllRegions();
    refetchAllZones();
  }, [refetch, refetchRegions, refetchAllRegions, refetchAllZones]);

  // Pagination (0-based → 1-based)
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Filter Apply / Clear
  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      zoneId: zoneFilter || undefined,
      regionId: regionFilter || undefined,
      areaCode: areaCodeFilter.trim() || undefined,
      areaName: areaNameFilter.trim() || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [zoneFilter, regionFilter, areaCodeFilter, areaNameFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setZoneFilter('');
    setRegionFilter('');
    setAreaCodeFilter('');
    setAreaNameFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.zoneId) count++;
    if (queryParams.regionId) count++;
    if (queryParams.areaCode) count++;
    if (queryParams.areaName) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.zoneId, queryParams.regionId, queryParams.areaCode, queryParams.areaName, queryParams.statusCode]);

  // ----- Grid columns -----
  const columns: SyutGridColumn<Area>[] = useMemo(() => [
    {
      id: 'zoneName',
      header: 'Zone',
      accessorKey: 'zoneName',
      size: 140,
      enableSorting: true,
      cell: ({ row }) => row.original.zoneName ?? row.original.zoneCode ?? '-',
    },
    {
      id: 'regionName',
      header: 'Region',
      accessorKey: 'regionName',
      size: 180,
      enableSorting: true,
      cell: ({ row }) => row.original.regionName ?? row.original.regionCode ?? '-',
    },
    {
      id: 'areaCode',
      header: 'Area Code',
      accessorKey: 'areaCode',
      size: 140,
      enableSorting: true,
    },
    {
      id: 'areaName',
      header: 'Area Name',
      accessorKey: 'areaName',
      size: 240,
      enableSorting: true,
      flex: true,
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const a = row.original;
        const code = a.deletedAt ? 'DELETED' : (a.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={a.deletedAt ? `Deleted at ${new Date(a.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try {
          return new Date(v).toLocaleString();
        } catch {
          return v;
        }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  // ----- Row actions -----
  const getRowActions = useCallback((area: Area): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(area) },
    { id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={16} />,
      onClick: () => handleEdit(area),
    },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(area) },
    { id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(area),
      variant: 'danger',
    },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  // ----- Header action buttons -----
  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add New Area',
      onClick: handleAddNew,
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Area Master"
        description="Third level of the branch reporting hierarchy. Each area rolls up into a region."
        icon={<IconSystem.tag size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, region, zone...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No areas found"
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((a) => (
                    <div
                      key={String(a.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(a)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(a)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {a.zoneName ?? a.zoneCode} · {a.regionName ?? a.regionCode} · {a.areaCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {a.areaName}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: a.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {a.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Zone"
                options={zoneFilterOptions}
                value={zoneFilter}
                onChange={(v) => setZoneFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Region"
                options={regionFilterOptions}
                value={regionFilter}
                onChange={(v) => setRegionFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Area Code
              </label>
              <input
                type="text"
                className="form-input"
                value={areaCodeFilter}
                onChange={(e) => setAreaCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Area Name
              </label>
              <input
                type="text"
                className="form-input"
                value={areaNameFilter}
                onChange={(e) => setAreaNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          areaToDelete
            ? `Are you sure you want to delete area "${areaToDelete.areaCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setAreaToDelete(null);
          }
        }}
      />

      <AreaFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedArea}
        regionOptions={regionOptions}
        seedRegionOptions={seedRegionOptions}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default AreaMasterScreen;
